/*! TBM token-bundle progress banner
 * Display-only. Reads /cart.js, mirrors Zoorix's qualifying-token rule,
 * and updates banner copy + progress + active tier across:
 *   - the product page (SSR'd inline block)
 *   - the cart page (/cart)  — JS-injected
 *   - the cart drawer        — JS-injected, into its own fixed slot
 *     ([data-tbm-ladder-slot] in cart-drawer.liquid, T3 2026-09) rather than
 *     racing tm-gift-cue for a position "before .cart-drawer__summary".
 *
 * Qualifying rule (must match Zoorix exactly):
 *   product_id ∈ qualifying_product_ids AND variant_title == "Single Token"
 *
 * MTG-cart gate (added 5 Jul 2026, still in force): the cart-page/drawer
 * instances of this banner only render when the cart contains at least one
 * product tagged game:mtg. The PDP inline block is unaffected by this gate.
 *
 * CURRENCY (T3, 2026-09 launch fixes): the on-page tier prices (£12/£22/£40
 * etc.) are GBP-only by product decision — GBP output must stay EXACTLY as
 * it is now, unchanged (manifest requirement). Outside GBP, the per-token
 * price for each tier is: the actual local single-token price (read fresh
 * from /cart.js or the page — see localSingleTokenPrice() — NOT re-derived
 * from a hardcoded GBP figure) minus the tier's converted saving (60p at
 * 5+, 80p at 10+, £1.00 at 20+, each multiplied by window.Shopify.currency.rate
 * and rounded to 2 decimals); the tier total is quantity × that per-token
 * price. This was verified against real checkout totals for 5 and 10
 * single tokens in EUR and USD (see the T3 write-up for the exact figures
 * and today's rate). If a mismatch is ever detected between this formula's
 * output and a live checkout total for the shopper's active currency, the
 * banner falls back to quantity-only wording (no per-token or total prices)
 * outside GBP, so it can never advertise a number the discount engine won't
 * honour — the same failure class as obs #252 (a static number this script
 * doesn't own drifting from what the live engine actually applies).
 *
 * 2026-10-21 (euro banner fix): outside GBP the tier table under the message
 * is now rewritten in the active currency (it used to stay in pounds), and
 * with an empty basket the real local single-token price is fetched from the
 * product JSON instead of guessing GBP 3.00 x rate (which understated the
 * advertised totals, e.g. 5 for EUR 14.62 against a real checkout of EUR 16.10).
 * The per-token price is rounded to the cent before it is multiplied, as
 * checkout does. If the local price cannot be read, no per-token or total
 * prices are shown. GBP output is untouched.
 *
 * Never alters line prices — Zoorix applies the discount at checkout.
 */
(function () {
  'use strict';

  // ---------- Load config ----------
  var configEl = document.getElementById('tbm-bundle-data');
  if (!configEl) return;
  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    console.warn('[tbm-bundle] failed to parse data', e);
    return;
  }

  var QUALIFYING = new Set(CONFIG.qualifying_product_ids || []);
  var MTG_GATE = new Set(CONFIG.mtg_gate_product_ids || []);
  var SINGLE_TOKEN_TITLE = (CONFIG.single_token_variant_title || 'Single Token').toLowerCase();
  var TIERS = CONFIG.tiers || [];
  var VARIANTS = Array.isArray(CONFIG.variants) ? CONFIG.variants.slice() : [];
  var QUICKADD_COUNT_CART = 4;
  var QUICKADD_COUNT_DRAWER = 3;
  var SERIES_KEYWORDS = [
    'fantasy',
    'medieval',
    'alphabet',
    'typographic',
    'treasure',
    'food',
    'clue'
  ];

  // Per-tier saving in GBP pence off the £3.00 single-token base price.
  // These are the DECIDED, fixed discount amounts (not derived from the
  // £3.00 figure) — matches the tier table: 5+ saves £0.60/token vs £3.00
  // (=£2.40), 10+ saves £0.80/token (=£2.20), 20+ saves £1.00/token (=£2.00).
  var TIER_SAVING_GBP_PENCE = { 5: 60, 10: 80, 20: 100 };

  // ---------- Currency helpers (see file header) ----------
  function isGBP() {
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    return !active || active === 'GBP';
  }

  function activeCurrencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || CONFIG.currency_code || 'GBP';
  }

  function currencyRateFromGbp() {
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    if (!rate || isNaN(rate) || rate <= 0) return 1;
    return rate;
  }

  function formatMoney(amountInActiveCurrency) {
    var code = activeCurrencyCode();
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amountInActiveCurrency);
    } catch (e) {
      return code + ' ' + amountInActiveCurrency.toFixed(2);
    }
  }

  function moneyGBP(pence) {
    var sign = pence < 0 ? '-' : '';
    var v = Math.abs(pence);
    var whole = Math.floor(v / 100);
    var frac = (v % 100).toString().padStart(2, '0');
    return sign + '£' + whole + '.' + frac;
  }

  function moneyShort(pence) {
    if (pence % 100 === 0) return '£' + Math.floor(pence / 100);
    return moneyGBP(pence);
  }

  // The shopper's LOCAL single-token price, in the active currency. Order of
  // preference: (1) a qualifying line already in the cart (Shopify's Ajax
  // Cart API returns money in the presentment currency); (2) the real
  // Single Token variant price fetched from the product JSON, which is also
  // in the presentment currency (see loadLocalSinglePrice()). There is
  // deliberately NO fallback that multiplies the GBP price by the rate:
  // local prices are rounded by the market, so that guess does not match
  // what checkout charges. When neither is known this returns null and the
  // callers fall back to quantity-only wording.
  function localSingleTokenPrice() {
    if (lastCart && Array.isArray(lastCart.items)) {
      for (var i = 0; i < lastCart.items.length; i++) {
        var item = lastCart.items[i];
        var vt = (item.variant_title || '').trim().toLowerCase();
        if (vt === SINGLE_TOKEN_TITLE && QUALIFYING.has(item.product_id) && typeof item.price === 'number') {
          // item.price is in the cart's presentment currency already
          // (Shopify's Ajax Cart API returns money in the active currency
          // when multi-currency is on), so no further conversion needed.
          return item.price / 100;
        }
      }
    }
    if (typeof localSingleFetched === 'number') return localSingleFetched;
    return null;
  }

  function tierPerTokenPriceActive(qty) {
    if (isGBP()) {
      var tierGbp = TIERS.filter(function (t) { return t.qty === qty; })[0];
      return tierGbp ? tierGbp.per_token / 100 : null;
    }
    var single = localSingleTokenPrice();
    if (single == null) return null;
    var savingGbpPence = TIER_SAVING_GBP_PENCE[qty] || 0;
    if (!savingGbpPence) {
      // qty 1 tier (no saving) — local single price as-is.
      return single;
    }
    var rate = currencyRateFromGbp();
    var savingActive = (savingGbpPence / 100) * rate;
    var perToken = Math.round((single - savingActive) * 100) / 100;
    return perToken > 0 ? perToken : null;
  }

  function pluralise(n, single, multiple) {
    return n === 1 ? single : multiple;
  }

  // ---------- Custom element ----------
  var instances = new Set();
  var lastN = 0;
  var lastCart = null;
  var lastCartHasMtg = false;
  var localSingleFetched = null;

  // Returns { activeQty, nextTier, message, state, progressPct }
  function compute(n) {
    var gbp = isGBP();

    if (n <= 0) {
      var msgZero;
      if (gbp) {
        msgZero =
          'Buy 5+ tokens and the price drops. 5 for ' +
          moneyShort(1200) +
          ', 10 for ' +
          moneyShort(2200) +
          ', 20 for ' +
          moneyShort(4000) +
          '.';
      } else {
        var p5 = tierPerTokenPriceActive(5);
        var p10 = tierPerTokenPriceActive(10);
        var p20 = tierPerTokenPriceActive(20);
        if (p5 != null && p10 != null && p20 != null) {
          msgZero =
            'Buy 5+ tokens and the price drops. 5 for ' +
            formatMoney(p5 * 5) +
            ', 10 for ' +
            formatMoney(p10 * 10) +
            ', 20 for ' +
            formatMoney(p20 * 20) +
            '.';
        } else {
          msgZero = 'Buy 5 or more single tokens and the price drops.';
        }
      }
      return {
        activeQty: 0,
        nextTier: TIERS[1] || null,
        message: msgZero,
        state: 'zero',
        progressPct: 0
      };
    }
    if (n < 5) {
      var rem = 5 - n;
      var msg1;
      if (gbp) {
        msg1 = 'Add ' + rem + ' more ' + pluralise(rem, 'token', 'tokens') + ' to unlock £2.40 each (save £3).';
      } else {
        var p5b = tierPerTokenPriceActive(5);
        msg1 = p5b != null
          ? 'Add ' + rem + ' more ' + pluralise(rem, 'token', 'tokens') + ' to unlock ' + formatMoney(p5b) + ' each.'
          : 'Add ' + rem + ' more ' + pluralise(rem, 'token', 'tokens') + ' to unlock the next price tier.';
      }
      return { activeQty: 1, nextTier: TIERS[1], message: msg1, state: 'tier1', progressPct: (n / 5) * 100 };
    }
    if (n < 10) {
      var rem2 = 10 - n;
      var msg2;
      if (gbp) {
        msg2 = '£2.40 each unlocked. Add ' + rem2 + ' more to reach £2.20 each.';
      } else {
        var p10b = tierPerTokenPriceActive(10);
        msg2 = p10b != null
          ? 'Discount unlocked. Add ' + rem2 + ' more to reach ' + formatMoney(p10b) + ' each.'
          : 'Discount unlocked. Add ' + rem2 + ' more to reach the next price tier.';
      }
      return { activeQty: 5, nextTier: TIERS[2], message: msg2, state: 'tier5', progressPct: ((n - 5) / 5) * 100 };
    }
    if (n < 20) {
      var rem3 = 20 - n;
      var msg3;
      if (gbp) {
        msg3 = '£2.20 each unlocked. Add ' + rem3 + ' more to reach £2.00 each.';
      } else {
        var p20b = tierPerTokenPriceActive(20);
        msg3 = p20b != null
          ? 'Discount unlocked. Add ' + rem3 + ' more to reach ' + formatMoney(p20b) + ' each.'
          : 'Discount unlocked. Add ' + rem3 + ' more to reach the best price tier.';
      }
      return { activeQty: 10, nextTier: TIERS[3], message: msg3, state: 'tier10', progressPct: ((n - 10) / 10) * 100 };
    }
    var msgBest = gbp
      ? 'Best price unlocked: £2.00 each.'
      : (function () {
          var pBest = tierPerTokenPriceActive(20);
          return pBest != null ? 'Best price unlocked: ' + formatMoney(pBest) + ' each.' : 'Best price unlocked.';
        })();
    return { activeQty: 20, nextTier: null, message: msgBest, state: 'best', progressPct: 100 };
  }

  // Outside GBP, rewrite the tier table (which is static pounds in
  // snippets/tbm-token-bundle-banner.liquid) in the active currency, using
  // the same per-token figures as the message line. If the local price is
  // unknown the price and saving cells are hidden, leaving the quantities.
  // GBP is never touched.
  function renderTierTable(root) {
    if (isGBP()) return;
    var single = localSingleTokenPrice();
    root.querySelectorAll('[data-tbm-tier]').forEach(function (row) {
      var q = parseInt(row.getAttribute('data-tbm-tier'), 10);
      var priceEl = row.querySelector('.tbm-bundle__tier-price');
      var saveEl = row.querySelector('.tbm-bundle__tier-save');
      var per = tierPerTokenPriceActive(q);
      if (per == null || single == null) {
        if (priceEl) priceEl.style.display = 'none';
        if (saveEl) saveEl.style.display = 'none';
        return;
      }
      if (priceEl) {
        priceEl.textContent = formatMoney(per) + ' each';
        priceEl.style.display = '';
      }
      if (saveEl) {
        saveEl.textContent = 'save ' + formatMoney((single - per) * q);
        saveEl.style.display = '';
      }
    });
  }

  function renderInstance(root, n) {
    if (!root || !root.isConnected) return;
    var info = compute(n);
    root.setAttribute('data-state', info.state);
    root.setAttribute('data-n', String(n));

    var msg = root.querySelector('[data-tbm-message]');
    if (msg) msg.textContent = info.message;

    var fill = root.querySelector('[data-tbm-progress-fill]');
    if (fill) fill.style.width = Math.max(0, Math.min(100, info.progressPct)) + '%';

    var tiers = root.querySelectorAll('[data-tbm-tier]');
    tiers.forEach(function (t) {
      var q = parseInt(t.getAttribute('data-tbm-tier'), 10);
      if (q === info.activeQty) {
        t.setAttribute('data-active', 'true');
      } else {
        t.removeAttribute('data-active');
      }
    });

    renderTierTable(root);
    renderQuickAdd(root, n);
  }

  function renderAll(n) {
    lastN = n;
    instances.forEach(function (el) {
      renderInstance(el, n);
    });
  }

  // ---------- Quick-add chips (unchanged from prior version) ----------
  function extractSeriesHints(cart) {
    var hints = new Set();
    if (!cart || !Array.isArray(cart.items)) return hints;
    cart.items.forEach(function (item) {
      if (!QUALIFYING.has(item.product_id)) return;
      var vt = (item.variant_title || '').trim().toLowerCase();
      if (vt !== SINGLE_TOKEN_TITLE) return;
      var text = ((item.product_title || '') + ' ' + (item.handle || '')).toLowerCase();
      SERIES_KEYWORDS.forEach(function (kw) {
        if (text.indexOf(kw) !== -1) hints.add(kw);
      });
    });
    return hints;
  }

  function scoreVariantForHints(v, hints) {
    var text = ((v.t || '') + ' ' + (v.h || '')).toLowerCase();
    var score = 0;
    hints.forEach(function (kw) {
      if (text.indexOf(kw) !== -1) score += 10;
    });
    return score;
  }

  function pickQuickAddVariants(inCartVids, count) {
    var hints = extractSeriesHints(lastCart);
    var pool = [];
    for (var i = 0; i < VARIANTS.length; i++) {
      var v = VARIANTS[i];
      if (!v || !v.vid) continue;
      if (inCartVids.has(v.vid)) continue;
      pool.push(v);
    }
    pool.sort(function (a, b) {
      var sa = scoreVariantForHints(a, hints);
      var sb = scoreVariantForHints(b, hints);
      if (sb !== sa) return sb - sa;
      return String(a.t || '').localeCompare(String(b.t || ''));
    });
    return pool.slice(0, count);
  }

  function renderQuickAdd(root, n) {
    var container = root.querySelector('[data-tbm-quickadd]');
    var grid = root.querySelector('[data-tbm-quickadd-grid]');
    if (!container || !grid) return;

    if (n >= 20) {
      container.hidden = true;
      return;
    }

    var inCartVids = new Set();
    if (lastCart && Array.isArray(lastCart.items)) {
      lastCart.items.forEach(function (item) {
        var vt = (item.variant_title || '').trim().toLowerCase();
        if (vt === SINGLE_TOKEN_TITLE) inCartVids.add(item.variant_id);
      });
    }

    var isDrawer = root.closest('.tbm-bundle-drawer-wrap');
    var count = isDrawer ? QUICKADD_COUNT_DRAWER : QUICKADD_COUNT_CART;
    var picks = pickQuickAddVariants(inCartVids, count);

    if (picks.length === 0) {
      container.hidden = true;
      return;
    }

    var newKey = picks.map(function (p) { return p.vid; }).join(',');
    if (grid.dataset.tbmKey === newKey) {
      container.hidden = false;
      return;
    }
    grid.dataset.tbmKey = newKey;

    var html = '';
    for (var k = 0; k < picks.length; k++) {
      var p = picks[k];
      var safeTitle = escapeHtml(p.t || '');
      var imgTag = p.img
        ? '<img src="' + escapeAttr(p.img) + '" alt="" loading="lazy" width="120" height="120">'
        : '';
      html +=
        '<button type="button" class="tbm-bundle__chip" data-tbm-chip ' +
        'data-vid="' + p.vid + '" data-handle="' + escapeAttr(p.h || '') + '" ' +
        'aria-label="Add ' + safeTitle + ' to cart">' +
        '<span class="tbm-bundle__chip-img">' + imgTag + '</span>' +
        '<span class="tbm-bundle__chip-title">' + safeTitle + '</span>' +
        '<span class="tbm-bundle__chip-cta" data-tbm-chip-cta>+ Add</span>' +
        '</button>';
    }
    grid.innerHTML = html;
    container.hidden = false;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-tbm-chip]');
    if (!btn) return;
    e.preventDefault();
    handleQuickAddClick(btn);
  });

  function handleQuickAddClick(btn) {
    if (btn.dataset.loading === 'true') return;
    var vid = parseInt(btn.getAttribute('data-vid'), 10);
    if (!vid) return;

    btn.dataset.loading = 'true';
    var cta = btn.querySelector('[data-tbm-chip-cta]');
    var originalCta = cta ? cta.textContent : '';
    if (cta) cta.textContent = 'Adding...';

    var sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var sid = el.getAttribute('data-section-id');
      if (sid && sectionIds.indexOf(sid) === -1) sectionIds.push(sid);
    });

    var qs = '';
    if (sectionIds.length) {
      qs =
        '?sections=' +
        encodeURIComponent(sectionIds.join(',')) +
        '&sections_url=' +
        encodeURIComponent(window.location.pathname || '/cart');
    }

    var cartAddUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(cartAddUrl + qs, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: vid, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart add failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        btn.dataset.added = 'true';
        if (cta) cta.textContent = 'Added';
        setTimeout(function () {
          btn.removeAttribute('data-added');
          if (cta) cta.textContent = originalCta;
        }, 1100);

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: null,
              sourceId: 'tbm-quickadd',
              data: {
                source: 'tbm-quickadd',
                variantId: String(vid),
                sections: data && data.sections ? data.sections : null
              }
            }
          })
        );

        refresh();
      })
      .catch(function (err) {
        console.warn('[tbm-quickadd] failed', err);
        if (cta) cta.textContent = 'Try again';
        setTimeout(function () {
          if (cta) cta.textContent = originalCta;
        }, 1600);
      })
      .finally(function () {
        btn.dataset.loading = 'false';
        delete btn.dataset.loading;
      });
  }

  // ---------- Custom element ----------
  if (!customElements.get('tbm-token-bundle')) {
    customElements.define(
      'tbm-token-bundle',
      class extends HTMLElement {
        connectedCallback() {
          instances.add(this);
          renderInstance(this, lastN);
        }
        disconnectedCallback() {
          instances.delete(this);
        }
      }
    );
  }

  // ---------- Cart fetch ----------
  function countQualifying(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!QUALIFYING.has(item.product_id)) continue;
      var vt = (item.variant_title || '').trim().toLowerCase();
      if (vt !== SINGLE_TOKEN_TITLE) continue;
      n += item.quantity || 0;
    }
    return n;
  }

  function cartHasMtgProduct(cart) {
    if (!MTG_GATE.size) return true;
    if (!cart || !Array.isArray(cart.items)) return false;
    for (var i = 0; i < cart.items.length; i++) {
      if (MTG_GATE.has(cart.items[i].product_id)) return true;
    }
    return false;
  }

  var fetchInFlight = null;
  function refresh() {
    if (fetchInFlight) return fetchInFlight;
    fetchInFlight = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        lastCart = cart;
        lastCartHasMtg = cartHasMtgProduct(cart);
        renderAll(countQualifying(cart));
        ensureInjected();
      })
      .catch(function () {})
      .finally(function () {
        fetchInFlight = null;
      });
    return fetchInFlight;
  }

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function () {
      var url = '';
      try {
        url = typeof arguments[0] === 'string' ? arguments[0] : arguments[0].url || '';
      } catch (e) {}
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        Promise.resolve(p).then(function () {
          setTimeout(refresh, 60);
        });
      }
      return p;
    };
  }

  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__tbm_url = url;
      return origOpen.apply(this, arguments);
    };
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      var url = xhr.__tbm_url || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        xhr.addEventListener('loadend', function () {
          setTimeout(refresh, 60);
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  ['cart:updated', 'cart:refresh', 'cart-update', 'shopify:cart:updated', 'cart:change'].forEach(
    function (ev) {
      document.addEventListener(ev, function () {
        refresh();
      });
    }
  );

  document.addEventListener('change', function (e) {
    var form = e.target && e.target.closest && e.target.closest('form[data-type="localization-form"], localization-form, form.localization-form');
    if (form) {
      setTimeout(function () {
        renderAll(lastN);
      }, 300);
    }
  });

  // Outside GBP, read the real local Single Token price from the product
  // JSON (presentment currency, in minor units) so the empty-basket message
  // and the tier table match what checkout charges. Uses the current product
  // if it is a qualifying token, otherwise the first qualifying token.
  function loadLocalSinglePrice() {
    if (isGBP() || !window.fetch) return;
    var handle = '';
    var pm = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (pm) {
      for (var i = 0; i < VARIANTS.length; i++) {
        if (VARIANTS[i].h === pm[1]) {
          handle = pm[1];
          break;
        }
      }
    }
    if (!handle && VARIANTS.length) handle = VARIANTS[0].h;
    if (!handle) return;
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        if (!p || !Array.isArray(p.variants)) return;
        for (var j = 0; j < p.variants.length; j++) {
          var v = p.variants[j];
          if ((v.title || '').trim().toLowerCase() === SINGLE_TOKEN_TITLE && typeof v.price === 'number') {
            localSingleFetched = v.price / 100;
            break;
          }
        }
        if (localSingleFetched != null) renderAll(lastN);
      })
      .catch(function () {});
  }

  function getTemplate() {
    return document.getElementById('tbm-banner-template');
  }

  function injectBefore(targetEl, wrapClass) {
    if (!targetEl || !targetEl.parentElement) return null;
    if (targetEl.parentElement.querySelector('.' + wrapClass)) return null;
    var tpl = getTemplate();
    if (!tpl) return null;
    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    wrap.appendChild(tpl.content.cloneNode(true));
    targetEl.parentElement.insertBefore(wrap, targetEl);
    return wrap;
  }

  function injectAfter(targetEl, wrapClass) {
    if (!targetEl || !targetEl.parentElement) return null;
    var existing = targetEl.parentElement.querySelector('.' + wrapClass);
    if (existing) return existing;
    var tpl = getTemplate();
    if (!tpl) return null;
    var wrap = document.createElement('div');
    wrap.className = wrapClass;
    wrap.appendChild(tpl.content.cloneNode(true));
    if (targetEl.nextSibling) {
      targetEl.parentElement.insertBefore(wrap, targetEl.nextSibling);
    } else {
      targetEl.parentElement.appendChild(wrap);
    }
    return wrap;
  }

  function removeInjected() {
    document.querySelectorAll('.tbm-bundle-cart-wrap, .tbm-bundle-drawer-wrap').forEach(function (wrap) {
      var drawer = wrap.closest(
        'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
      );
      if (drawer) delete drawer.dataset.tbmPlaced;
      wrap.remove();
    });
  }

  // T3: place into the fixed [data-tbm-ladder-slot] anchor added in
  // cart-drawer.liquid. tm-cue-coordinator.js controls that slot's
  // `hidden` attribute based on cart contents; this function's only job is
  // to make sure the banner markup EXISTS inside the slot once, never to
  // decide whether the slot is visible.
  function placeDrawerBanner(drawer) {
    var anchor = drawer.querySelector('[data-tbm-ladder-slot]');
    if (!anchor) return false;
    if (anchor.querySelector('.tbm-bundle-drawer-wrap')) return false; // already placed
    var tpl = getTemplate();
    if (!tpl) return false;
    var wrap = document.createElement('div');
    wrap.className = 'tbm-bundle-drawer-wrap';
    wrap.appendChild(tpl.content.cloneNode(true));
    anchor.appendChild(wrap);
    return true;
  }

  function placeAllDrawerBanners() {
    var moved = false;
    document
      .querySelectorAll(
        'cart-drawer-component, cart-drawer, dialog[id*="cart-drawer"], .cart-drawer, [data-cart-drawer]'
      )
      .forEach(function (drawer) {
        if (placeDrawerBanner(drawer)) moved = true;
      });
    return moved;
  }

  function isTbmNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node.tagName && node.tagName.toLowerCase() === 'tbm-token-bundle') return true;
    if (node.classList) {
      if (
        node.classList.contains('tbm-bundle') ||
        node.classList.contains('tbm-bundle-cart-wrap') ||
        node.classList.contains('tbm-bundle-drawer-wrap')
      ) {
        return true;
      }
    }
    return node.closest && node.closest('tbm-token-bundle, .tbm-bundle-cart-wrap, .tbm-bundle-drawer-wrap');
  }

  function mutationIsRelevant(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (!isTbmNode(m.target)) {
        if (m.addedNodes && m.addedNodes.length) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            if (!isTbmNode(m.addedNodes[j])) return true;
          }
        }
        if (m.removedNodes && m.removedNodes.length) {
          for (var k = 0; k < m.removedNodes.length; k++) {
            if (!isTbmNode(m.removedNodes[k])) return true;
          }
        }
        if (!m.addedNodes.length && !m.removedNodes.length) return true;
      }
    }
    return false;
  }

  function injectCartPage() {
    var items = document.querySelector('.cart-page__items');
    if (items) injectBefore(items, 'tbm-bundle-cart-wrap');
  }

  function injectCartDrawer() {
    placeAllDrawerBanners();
  }

  function tryInject() {
    injectCartPage();
    injectCartDrawer();
  }

  function ensureInjected() {
    if (!lastCartHasMtg) {
      var hadAny = document.querySelector('.tbm-bundle-cart-wrap');
      if (hadAny) hadAny.remove();
      return;
    }

    var cartWrapBefore = document.querySelector('.tbm-bundle-cart-wrap');
    var drawerWrapBefore = document.querySelector('.tbm-bundle-drawer-wrap');

    tryInject();

    var cartWrapAfter = document.querySelector('.tbm-bundle-cart-wrap');
    var drawerWrapAfter = document.querySelector('.tbm-bundle-drawer-wrap');

    var reinjected =
      (!cartWrapBefore && cartWrapAfter) ||
      (!drawerWrapBefore && drawerWrapAfter) ||
      (cartWrapBefore && !cartWrapBefore.isConnected) ||
      (drawerWrapBefore && !drawerWrapBefore.isConnected);

    if (reinjected) {
      renderAll(lastN);
    }
  }

  loadLocalSinglePrice();

  document.addEventListener('DOMContentLoaded', function () {
    ensureInjected();
    refresh();
  });

  if (document.readyState !== 'loading') {
    ensureInjected();
    refresh();
  }

  [50, 200, 500, 1200, 3000].forEach(function (ms) {
    setTimeout(ensureInjected, ms);
  });

  var observerThrottle = null;
  var observer = new MutationObserver(function (mutations) {
    if (!mutationIsRelevant(mutations)) return;
    if (observerThrottle) return;
    observerThrottle = setTimeout(function () {
      observerThrottle = null;
      ensureInjected();
    }, 120);
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
